import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { API } from "@/lib/api";
import { useApi, Panel, PageHead, Stat, DataTable, StatusBadge, Tabs, inr } from "@/crm/ui";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, LineChart, Line,
} from "recharts";

const REPORTS = ["dashboard", "mis-daily", "mis-monthly", "lead", "bank", "location", "manager",
  "product", "employee", "partner", "login", "approval", "hold", "rejection", "disbursement",
  "commission", "incentive", "payout", "payout-ageing", "tat"];

const PERIODS = {
  "This Month": () => { const n = new Date(); return [new Date(n.getFullYear(), n.getMonth(), 1), n]; },
  "Last Month": () => { const n = new Date(); return [new Date(n.getFullYear(), n.getMonth() - 1, 1), new Date(n.getFullYear(), n.getMonth(), 0)]; },
  "This Quarter": () => { const n = new Date(); return [new Date(n.getFullYear(), Math.floor(n.getMonth() / 3) * 3, 1), n]; },
  "This Year": () => { const n = new Date(); return [new Date(n.getFullYear(), 0, 1), n]; },
};
const iso = (d) => d.toISOString().slice(0, 10);

const GROUP_COLS = [
  { key: "label", label: "Name" }, { key: "leads", label: "Leads", right: true },
  { key: "login", label: "Login", right: true },
  { key: "login_amount", label: "Login Amt", right: true, render: (r) => inr(r.login_amount) },
  { key: "approved", label: "Approved", right: true },
  { key: "approved_amount", label: "Approved Amt", right: true, render: (r) => inr(r.approved_amount) },
  { key: "hold", label: "Hold", right: true }, { key: "rejected", label: "Rejected", right: true },
  { key: "disbursed", label: "Disbursed", right: true },
  { key: "disbursed_amount", label: "Disbursed Amt", right: true, render: (r) => inr(r.disbursed_amount) },
  { key: "approval_rate", label: "Approval %", right: true },
  { key: "average_tat", label: "Avg TAT", right: true },
];

const EMPTY = {
  date_from: "", date_to: "", bank_id: "", location_id: "", manager_id: "", product_id: "",
  employee_id: "", partner_id: "", status: "", source: "",
};

export default function Reports() {
  const [params] = useSearchParams();
  const [report, setReport] = useState(params.get("report") || "dashboard");
  const [filters, setFilters] = useState(() => ({
    ...EMPTY,
    ...Object.fromEntries(Object.keys(EMPTY)
      .filter((k) => params.get(k))
      .map((k) => [k, params.get(k)])),
  }));
  const options = useApi("/crm/options");
  const meta = useApi("/crm/meta");

  const qs = useMemo(() => {
    const p = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) p.set(k, v); });
    return p.toString();
  }, [filters]);

  const data = useApi(`/crm/reports/${report}${qs ? `?${qs}` : ""}`, [report, qs]);
  const d = data.data || {};
  const cards = d.cards;
  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const drill = (next, extra = {}) => { setFilters((f) => ({ ...f, ...extra })); setReport(next); };
  const opts = options.data || {};
  const locOptions = (opts.locations || []).filter((l) => !filters.bank_id || l.bank_id === filters.bank_id);
  const mgrOptions = (opts.managers || []).filter((m) => !filters.bank_id || m.bank_id === filters.bank_id);

  const filterFields = [
    { key: "bank_id", label: "Bank", list: opts.banks, id: "bank_id", name: "bank_name" },
    { key: "location_id", label: "Bank Location", list: locOptions, id: "location_id", name: "location_name" },
    { key: "manager_id", label: "Bank Manager", list: mgrOptions, id: "manager_id", name: "manager_name" },
    { key: "product_id", label: "Product", list: opts.products, id: "product_id", name: "product_name" },
    { key: "employee_id", label: "Employee", list: opts.employees, id: "employee_id", name: "employee_name" },
    { key: "partner_id", label: "Partner", list: opts.partners, id: "partner_id", name: "partner_name" },
  ];

  return (
    <div className="space-y-6" data-testid="reports-page">
      <PageHead eyebrow="Reports" title="CRM Reports & MIS"
                subtitle="Every metric is calculated server-side and scoped to your role. Cards are clickable — they drill down into the matching report."
                actions={<span className="flex flex-wrap gap-2">
                  <Button variant="outline" className="bg-white" data-testid="export-report-btn"
                          onClick={() => window.open(`${API}/crm/reports/${report}/export${qs ? `?${qs}` : ""}`, "_blank")}>
                    <Download className="mr-2 h-4 w-4" /> CSV</Button>
                  <Button variant="outline" className="bg-white" data-testid="export-report-xlsx-btn"
                          onClick={() => window.open(`${API}/crm/reports/${report}/download?format=xlsx${qs ? `&${qs}` : ""}`, "_blank")}>
                    <Download className="mr-2 h-4 w-4" /> Excel</Button>
                  <Button variant="outline" className="bg-white" data-testid="export-report-pdf-btn"
                          onClick={() => window.open(`${API}/crm/reports/${report}/download?format=pdf${qs ? `&${qs}` : ""}`, "_blank")}>
                    <Download className="mr-2 h-4 w-4" /> PDF</Button>
                </span>} />

      <Panel title="Filters" testid="report-filters"
             aside={<Button size="sm" variant="ghost" onClick={() => setFilters(EMPTY)}
                            data-testid="clear-filters-btn">Clear all</Button>}>
        <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-5">
          <div className="space-y-1">
            <Label className="text-xs">Date from</Label>
            <Input type="date" value={filters.date_from} data-testid="filter-date-from"
                   onChange={(e) => set("date_from", e.target.value)} className="bg-white" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Date to</Label>
            <Input type="date" value={filters.date_to} data-testid="filter-date-to"
                   onChange={(e) => set("date_to", e.target.value)} className="bg-white" />
          </div>
          {filterFields.map((f) => (
            <div key={f.key} className="space-y-1">
              <Label className="text-xs">{f.label}</Label>
              <select value={filters[f.key]} onChange={(e) => set(f.key, e.target.value)}
                      data-testid={`filter-${f.key}`}
                      className="h-9 w-full rounded-md border border-input bg-white px-2 text-sm">
                <option value="">All</option>
                {(f.list || []).map((r) => (
                  <option key={r[f.id]} value={r[f.id]}>{r[f.name]}</option>))}
              </select>
            </div>
          ))}
          <div className="space-y-1">
            <Label className="text-xs">Application Status</Label>
            <select value={filters.status} onChange={(e) => set("status", e.target.value)}
                    data-testid="filter-status"
                    className="h-9 w-full rounded-md border border-input bg-white px-2 text-sm">
              <option value="">All</option>
              {(meta.data?.application_statuses || []).map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Lead Source</Label>
            <select value={filters.source} onChange={(e) => set("source", e.target.value)}
                    data-testid="filter-source"
                    className="h-9 w-full rounded-md border border-input bg-white px-2 text-sm">
              <option value="">All</option>
              {(opts.sources || []).map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          {Object.keys(PERIODS).map((p) => (
            <Button key={p} size="sm" variant="outline" className="bg-white text-[11px]"
                    data-testid={`period-${p.toLowerCase().replace(/\s+/g, "-")}`}
                    onClick={() => {
                      const [from, to] = PERIODS[p]();
                      setFilters((f) => ({ ...f, date_from: iso(from), date_to: iso(to) }));
                    }}>{p}</Button>))}
        </div>
      </Panel>

      <Tabs tabs={REPORTS} active={report} onChange={setReport} testid="report-tabs" />

      {cards && (
        <>
          <div className="grid gap-4 sm:grid-cols-3 xl:grid-cols-6">
            <Stat label="Total Leads" value={cards.total_leads} testid="mis-leads" onClick={() => drill("lead")} />
            <Stat label="Total Login" value={cards.total_login} testid="mis-login" onClick={() => drill("login")} />
            <Stat label="Login Amount" value={inr(cards.login_amount)} testid="mis-login-amount" onClick={() => drill("login")} />
            <Stat label="Approved" value={cards.approved} testid="mis-approved" onClick={() => drill("approval")} />
            <Stat label="Approved Amount" value={inr(cards.approved_amount)} testid="mis-approved-amount" onClick={() => drill("approval")} />
            <Stat label="Hold" value={cards.hold} testid="mis-hold" onClick={() => drill("hold")} />
            <Stat label="Rejected" value={cards.rejected} testid="mis-rejected" onClick={() => drill("rejection")} />
            <Stat label="Disbursed" value={cards.disbursed} testid="mis-disbursed" onClick={() => drill("disbursement")} />
            <Stat label="Disbursed Amount" value={inr(cards.disbursed_amount)} testid="mis-disbursed-amount" onClick={() => drill("disbursement")} />
            <Stat label="Pending Payout" value={inr(cards.pending_payout)} testid="mis-pending-payout" onClick={() => drill("payout-ageing")} />
            <Stat label="Total Commission" value={inr(cards.total_commission)} testid="mis-commission" onClick={() => drill("commission")} />
            <Stat label="Total Incentive" value={inr(cards.total_incentive)} testid="mis-incentive" onClick={() => drill("incentive")} />
          </div>

          <div className="grid gap-6 lg:grid-cols-2">
            <Panel title="Pipeline funnel" testid="funnel-panel">
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={d.funnel} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis type="number" fontSize={11} />
                  <YAxis type="category" dataKey="stage" width={110} fontSize={11} />
                  <Tooltip />
                  <Bar dataKey="count" name="Cases" fill="#1E3A8A" radius={[0, 3, 3, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Panel>
            <Panel title="Monthly trend" testid="mis-monthly-chart">
              <ResponsiveContainer width="100%" height={240}>
                <LineChart data={d.monthly}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis dataKey="month" fontSize={11} />
                  <YAxis fontSize={11} tickFormatter={(v) => `${Math.round(v / 100000)}L`} />
                  <Tooltip formatter={(v) => inr(v)} /><Legend />
                  <Line type="monotone" dataKey="login_amount" name="Login" stroke="#3B82F6" strokeWidth={2} />
                  <Line type="monotone" dataKey="disbursed_amount" name="Disbursed" stroke="#10B981" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </Panel>
          </div>

          <Panel title="Payout summary" testid="payout-summary-panel"
                 aside={<span className="text-xs text-muted-foreground">
                   Employee {inr(cards.employee_commission)} · Partner {inr(cards.partner_commission)} · Paid {inr(cards.paid_payout)}
                 </span>}>
            <DataTable testid="payout-summary-table" rows={d.payout_summary}
                       empty="No payouts generated yet."
                       columns={[{ key: "status", label: "Status", render: (r) => <StatusBadge value={r.status} /> },
                         { key: "count", label: "Payouts", right: true },
                         { key: "net_payable", label: "Net Payable", right: true, render: (r) => inr(r.net_payable) }]} />
          </Panel>

          {[["by_bank", "Bank", "bank_id"], ["by_employee", "Employee", "employee_id"],
            ["by_partner", "Partner", "partner_id"], ["by_product", "Product", "product_id"],
            ["by_location", "Location", "location_id"]].map(([k, title, filterKey]) => (
            <Panel key={k} title={`${title} performance`} testid={`mis-${k}`}
                   aside={<span className="text-xs text-muted-foreground">Click a row to drill down</span>}>
              <DataTable testid={`mis-${k}-table`} rows={d[k]} columns={GROUP_COLS}
                         onRowClick={(r) => drill("disbursement", { [filterKey]: r[filterKey] })} />
            </Panel>
          ))}
        </>
      )}

      {d.stages && (
        <Panel title="TAT summary (days)" testid="tat-panel">
          <DataTable testid="tat-table"
                     rows={Object.entries(d.stages).map(([k, v]) => ({ stage: k.replace(/_/g, " → "), ...v }))}
                     columns={[{ key: "stage", label: "Stage" }, { key: "count", label: "Cases", right: true },
                       { key: "average", label: "Average", right: true }, { key: "median", label: "Median", right: true },
                       { key: "max", label: "Maximum", right: true }]} />
          <div className="mt-6">
            <DataTable testid="tat-bank-table" rows={d.by_bank} columns={GROUP_COLS} />
          </div>
        </Panel>
      )}

      {d.rows && !cards && (
        <Panel title={`${report} report (${d.rows.length} rows)`} testid="report-rows-panel"
               aside={d.totals && <span className="text-xs text-muted-foreground">
                 {Object.entries(d.totals).map(([k, v]) => `${k}: ${inr(v)}`).join(" · ")}
               </span>}>
          <DataTable testid="report-rows-table" rows={d.rows} empty="No rows for the selected filters."
                     columns={(d.rows[0] ? Object.keys(d.rows[0]) : ["info"]).slice(0, 14).map((k) => ({
                       key: k, label: k.replace(/_/g, " "),
                       right: /amount|rate|count|days|payable|tat|share|incentive|tds/.test(k),
                       render: /amount|payable|share|commission|incentive|tds/.test(k) ? (r) => inr(r[k])
                         : /status|kyc/.test(k) ? (r) => <StatusBadge value={r[k]} /> : undefined,
                     }))} />
        </Panel>
      )}
    </div>
  );
}
